import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { canAccessPage } from '../pageAccess';

// ── Icons ──────────────────────────────────────────────────────────────────
const IconAlertCircle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="23 4 23 10 17 10"/>
    <polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);

const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const IconLock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const IconHistory = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <polyline points="12 8 12 12 14 14"/>
    <path d="M3.05 11a9 9 0 1 0 .5-4.5"/>
    <polyline points="3 3 3 7 7 7"/>
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
       strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
       strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="14"/>
    <line x1="12" y1="19" x2="12.01" y2="19"/>
  </svg>
);

const IconX = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
       strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// ── DNS Health Ring (SVG donut) ────────────────────────────────────────────
function DNSHealthRing({ score }) {
  const r    = 44;
  const circ = 2 * Math.PI * r;
  const dash = Math.max(0, Math.min(1, score / 100)) * circ;
  const color = score >= 90 ? 'var(--success)' : score >= 70 ? 'var(--warning)' : 'var(--danger)';
  const label = score >= 90 ? 'Excellent' : score >= 70 ? 'Needs attention' : 'Critical';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <svg viewBox="0 0 100 100" width="116" height="116" style={{ overflow: 'visible' }}>
        <circle cx="50" cy="50" r={r} fill="none"
                stroke="var(--border)" strokeWidth="8" />
        <circle cx="50" cy="50" r={r} fill="none"
                stroke={color} strokeWidth="8"
                strokeDasharray={`${dash} ${circ}`}
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
                style={{ transition: 'stroke-dasharray 0.6s ease' }} />
        <text x="50" y="46" textAnchor="middle" dominantBaseline="middle"
              fill={color} fontSize="19" fontWeight="600"
              fontFamily="var(--font-mono)">{score}%</text>
        <text x="50" y="62" textAnchor="middle" dominantBaseline="middle"
              fill="var(--text-muted)" fontSize="7.5"
              fontFamily="var(--font)">{label}</text>
      </svg>
    </div>
  );
}

// ── Status glyph for DNS record ────────────────────────────────────────────
function RecordIcon({ status }) {
  if (!status) return <span className="rec-glyph none">-</span>;
  if (status === 'match')
    return <span className="rec-glyph match" title="Match"><IconCheck /></span>;
  if (status === 'warning')
    return <span className="rec-glyph warning" title="Warning"><IconAlert /></span>;
  if (status === 'missing')
    return <span className="rec-glyph missing" title="Missing"><IconX /></span>;
  if (status === 'mismatch')
    return <span className="rec-glyph missing" title="Mismatch"><IconX /></span>;
  return <span className="rec-glyph none">-</span>;
}

// ── Skeleton loading row ───────────────────────────────────────────────────
function SkeletonRow({ cols = 4 }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0',
                  borderBottom: '1px solid var(--border)' }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} style={{
          flex: i === 0 ? 2 : 1,
          height: 14, borderRadius: 6,
          background: 'var(--bg-badge)',
          animation: 'pulse 1.5s ease-in-out infinite',
        }} />
      ))}
    </div>
  );
}

// ── Relative time formatter ────────────────────────────────────────────────
function relativeTime(dateStr) {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1)  return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)   return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Server card (uptime grid) ──────────────────────────────────────────────
function ServerCard({ monitor, index }) {
  const navigate = useNavigate();
  const isUp = monitor.status === 'up';

  return (
    <div
      className={`server-card rise ${isUp ? 'is-up' : 'is-down'}`}
      style={{ '--i': Math.min(index, 8) }}
      onClick={() => navigate('/uptime')}
      role="link"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && navigate('/uptime')}
    >
      <div className="server-card-top">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="server-name">{monitor.name}</div>
          <div className="server-host">{monitor.hostname || '-'}</div>
        </div>
        <span className="server-latency">
          {monitor.avgResponseTime ? '~' + monitor.avgResponseTime + 'ms' : '-'}
        </span>
      </div>

      <div className="server-bars">
        {monitor.bars && monitor.bars.length > 0 ? (
          monitor.bars.slice(-30).map((bar, idx) => (
            <div key={idx} className={`server-bar ${bar.status === 'up' ? 'up' : 'down'}`} />
          ))
        ) : (
          Array.from({ length: 20 }).map((_, idx) => (
            <div key={idx} className="server-bar none" />
          ))
        )}
      </div>

      <div className="server-card-foot">
        <span>{monitor.firstCheck ? relativeTime(monitor.firstCheck) : '-'}</span>
        <span>{monitor.lastCheck  ? relativeTime(monitor.lastCheck)  : '-'}</span>
      </div>
    </div>
  );
}

// ── Tile heading ───────────────────────────────────────────────────────────
function TileHead({ icon, title, linkText, onLink }) {
  return (
    <div className="tile-head">
      <div className="tile-title">
        {icon}
        <span>{title}</span>
      </div>
      {linkText && (
        <button className="tile-link" onClick={onLink}>
          {linkText} <IconArrow />
        </button>
      )}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────
const IconShieldCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

function Dashboard({ token, user }) {
  // The API returns no data for pages the user can't open; skip those calls
  // and hide their sections instead of showing empty tiles.
  const canUptime = canAccessPage(user, 'uptime');
  const canDns    = canAccessPage(user, 'dns');

  const navigate = useNavigate();

  const [stats, setStats] = useState({
    uptimeTotal: 0, uptimeUp: 0, uptimeDown: 0,
    dnsTotal: 0, dnsMatch: 0, dnsMismatch: 0,
  });
  const [loading, setLoading]           = useState(true);
  const [lastRefresh, setLastRefresh]   = useState(null);
  const [uptimeBars, setUptimeBars]     = useState([]);

  // DNS section state
  const [dnsHealth,   setDnsHealth]   = useState(null);
  const [dnsIssues,   setDnsIssues]   = useState([]);
  const [dnsChanges,  setDnsChanges]  = useState([]);
  const [dnsProblems, setDnsProblems] = useState([]);
  const [dnsLoading,  setDnsLoading]  = useState(true);

  // SSL section state
  const [sslSummary, setSslSummary] = useState(null);
  const [repSummary, setRepSummary] = useState(null); // null = no access or no data

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      const [uptimeRes, dnsRes] = await Promise.all([
        canUptime ? api.get('/uptime') : { data: [] },
        canDns    ? api.get('/dns')    : { data: [] },
      ]);

      const uptime = uptimeRes.data;
      const dns    = dnsRes.data;

      setStats({
        uptimeTotal:  uptime.length,
        uptimeUp:     uptime.filter(m => m.is_active).length,
        uptimeDown:   uptime.filter(m => !m.is_active).length,
        dnsTotal:     dns.length,
        dnsMatch:     dns.filter(m => m.health_score >= 4).length,
        dnsMismatch:  dns.filter(m => m.health_score < 4).length,
      });

      setLastRefresh(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setLoading(false);
    }

    // Fetch uptime bars
    try {
      const barsRes = await api.get('/dashboard/uptime-bars');
      setUptimeBars(barsRes.data);
    } catch (err) {
      console.error('Failed to fetch uptime bars:', err);
    }

    // Fetch SSL certificate summary
    try {
      const sslRes = await api.get('/dashboard/ssl-summary');
      setSslSummary(sslRes.data);
    } catch (err) {
      console.error('Failed to fetch SSL summary:', err);
    }

    // IP Reputation summary (own targets; null without page access)
    try {
      const repRes = await api.get('/dashboard/reputation-summary');
      setRepSummary(repRes.data);
    } catch (err) {
      console.error('Failed to fetch IP reputation summary:', err);
    }

    // Fetch DNS-specific sections in parallel
    if (!canDns) {
      setDnsLoading(false);
      return;
    }
    try {
      const [healthRes, issuesRes, changesRes, problemsRes] = await Promise.all([
        api.get('/dashboard/dns-health'),
        api.get('/dashboard/dns-issues'),
        api.get('/dashboard/dns-changes'),
        api.get('/dashboard/dns-top-problems'),
      ]);
      setDnsHealth(healthRes.data);
      setDnsIssues(issuesRes.data);
      setDnsChanges(changesRes.data);
      setDnsProblems(problemsRes.data);
    } catch (err) {
      console.error('Failed to fetch DNS section data:', err);
    } finally {
      setDnsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <span>Loading dashboard…</span>
      </div>
    );
  }

  const allUp = stats.uptimeDown === 0;

  return (
    <div>
      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="page-header rise" style={{ '--i': 0 }}>
        <div className="page-header-top">
          <div>
            <h1>Dashboard</h1>
            <p>System monitoring overview</p>
          </div>
          <div className="page-actions">
            {lastRefresh && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)',
                             fontFamily: 'var(--font-mono)' }}>
                updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button className="btn btn-secondary" onClick={fetchDashboardData}>
              <IconRefresh /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── Bento row 1: status hero ──────────────────────────────────────── */}
      {canUptime && (
        <div className="bento">
          <div className="tile hero-status b-12 rise" style={{ '--i': 1, padding: 0 }}>
            <div className="hero-status-main">
              <div className="hero-eyebrow">
                <span className={`pulse-dot ${allUp ? 'ok' : 'bad'}`} />
                <span>Live status</span>
              </div>
              <div className="hero-headline">
                {allUp
                  ? 'All systems operational'
                  : `${stats.uptimeDown} service${stats.uptimeDown === 1 ? '' : 's'} down`}
              </div>
              <div className="hero-sub">
                {stats.uptimeTotal} monitored service{stats.uptimeTotal === 1 ? '' : 's'}, checked every 30 seconds
              </div>
            </div>
            <div className="hero-status-stats">
              <div className="hero-stat" onClick={() => navigate('/uptime')}
                   role="link" tabIndex={0}
                   onKeyDown={e => e.key === 'Enter' && navigate('/uptime')}>
                <span className="hero-stat-num is-up">{stats.uptimeUp}</span>
                <span className="hero-stat-label">Up</span>
              </div>
              <div className="hero-stat" onClick={() => navigate('/uptime')}
                   role="link" tabIndex={0}
                   onKeyDown={e => e.key === 'Enter' && navigate('/uptime')}>
                <span className={`hero-stat-num ${stats.uptimeDown > 0 ? 'is-down' : 'is-dim'}`}>
                  {stats.uptimeDown}
                </span>
                <span className="hero-stat-label">Down</span>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* ── SSL certificates summary ──────────────────────────────────────── */}
      {sslSummary && sslSummary.total > 0 && (
        <div className="bento">
          <div className="tile b-12 rise" style={{ '--i': 2, cursor: 'pointer' }}
               onClick={() => navigate('/ssl')}
               role="link" tabIndex={0}
               onKeyDown={e => e.key === 'Enter' && navigate('/ssl')}>
            <TileHead
              icon={<IconLock />}
              title="SSL Certificates"
              linkText="View"
              onLink={e => { e.stopPropagation(); navigate('/ssl'); }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600,
                              color: 'var(--text-primary)', lineHeight: 1 }}>
                  {sslSummary.total}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                  certificate{sslSummary.total === 1 ? '' : 's'} tracked
                </div>
              </div>

              {[
                { label: 'valid',           val: sslSummary.valid,    color: 'var(--success)' },
                { label: 'expiring soon',   val: sslSummary.expiring, color: 'var(--warning)' },
                { label: 'expired',         val: sslSummary.expired,  color: 'var(--danger)'  },
                { label: 'errors',          val: sslSummary.errored,  color: 'var(--text-muted)' },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600,
                                color: s.val > 0 ? s.color : 'var(--text-muted)', lineHeight: 1 }}>
                    {s.val}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}

              {typeof sslSummary.soonestDaysRemaining === 'number' && (
                <div style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  Next expiry in{' '}
                  <strong style={{
                    fontFamily: 'var(--font-mono)',
                    color: sslSummary.soonestDaysRemaining <= 7 ? 'var(--danger)'
                         : sslSummary.soonestDaysRemaining <= 30 ? 'var(--warning)'
                         : 'var(--success)',
                  }}>
                    {sslSummary.soonestDaysRemaining}d
                  </strong>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── IP Reputation summary (the user's own targets) ─────────────────── */}
      {repSummary && repSummary.total > 0 && (
        <div className="bento">
          <div className="tile b-12 rise" style={{ '--i': 2, cursor: 'pointer' }}
               onClick={() => navigate('/reputation')}
               role="link" tabIndex={0}
               onKeyDown={e => e.key === 'Enter' && navigate('/reputation')}>
            <TileHead
              icon={<IconShieldCheck />}
              title="IP Reputation"
              linkText="View"
              onLink={e => { e.stopPropagation(); navigate('/reputation'); }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
              {[
                { label: 'target' + (repSummary.total === 1 ? '' : 's'), val: repSummary.total, color: 'var(--text-primary)' },
                { label: 'clean',   val: repSummary.clean,   color: 'var(--success)' },
                { label: 'listed',  val: repSummary.listed,  color: 'var(--danger)' },
                { label: 'unknown', val: repSummary.unknown + repSummary.pending, color: 'var(--text-muted)' },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600,
                                color: s.val > 0 ? s.color : 'var(--text-muted)', lineHeight: 1 }}>
                    {s.val}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Server cards grid ─────────────────────────────────────────────── */}
      {uptimeBars.length > 0 && (
        <div className="server-grid">
          {uptimeBars.map((m, i) => <ServerCard key={m.id} monitor={m} index={i + 3} />)}
        </div>
      )}

      {/* ── DNS sections (bottom of page) ─────────────────────────────────── */}
      {canDns && (
        <div className="bento">
          <div className="tile b-4 rise" style={{ '--i': 3, cursor: 'pointer' }}
               onClick={() => navigate('/dns')}
               role="link" tabIndex={0}
               onKeyDown={e => e.key === 'Enter' && navigate('/dns')}>
            <TileHead
              icon={<IconShield />}
              title="DNS Health"
              linkText="View"
              onLink={e => { e.stopPropagation(); navigate('/dns'); }}
            />
            {dnsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                <div className="spinner" />
              </div>
            ) : dnsHealth ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <DNSHealthRing score={dnsHealth.healthScore} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600,
                                  color: 'var(--text-primary)', lineHeight: 1 }}>
                      {dnsHealth.totalDomains}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>domains</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      <strong style={{ color: 'var(--success)' }}>{dnsHealth.healthyDomains}</strong> healthy
                    </span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      <strong style={{ color: dnsHealth.issuesDomains > 0 ? 'var(--danger)' : 'var(--text-primary)' }}>
                        {dnsHealth.issuesDomains}
                      </strong> with issues
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
                            padding: '20px 0' }}>
                No DNS data yet. Run a check to see results.
              </div>
            )}
          </div>

          <div className="tile b-8 rise" style={{ '--i': 4 }}>
            <TileHead
              icon={<IconAlertCircle />}
              title="DNS Issues"
              linkText="View all"
              onLink={() => navigate('/dns')}
            />
            {dnsLoading ? (
              <>
                <SkeletonRow cols={5} />
                <SkeletonRow cols={5} />
                <SkeletonRow cols={5} />
              </>
            ) : dnsIssues.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: 8, padding: '18px 0', color: 'var(--success)',
                            fontSize: 13, fontWeight: 600 }}>
                <span className="rec-glyph match"><IconCheck /></span>
                All monitored domains are healthy
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 0', color: 'var(--text-muted)',
                                   fontWeight: 600, fontSize: 11 }}>Domain</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: 'var(--text-muted)',
                                   fontWeight: 600, fontSize: 11 }}>SPF</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: 'var(--text-muted)',
                                   fontWeight: 600, fontSize: 11 }}>MX</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: 'var(--text-muted)',
                                   fontWeight: 600, fontSize: 11 }}>DMARC</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', color: 'var(--text-muted)',
                                   fontWeight: 600, fontSize: 11 }}>DKIM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dnsIssues.map((d, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)',
                                           cursor: 'pointer' }}
                          onClick={() => navigate('/dns')}>
                        <td style={{ padding: '8px 0', fontFamily: 'var(--font-mono)',
                                     color: 'var(--text-primary)', maxWidth: 160,
                                     overflow: 'hidden', textOverflow: 'ellipsis',
                                     whiteSpace: 'nowrap' }}
                            title={d.domain}>{d.domain}</td>
                        <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                          <RecordIcon status={d.spf_status} />
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                          <RecordIcon status={d.mx_status} />
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                          <RecordIcon status={d.dmarc_status} />
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                          <RecordIcon status={d.dkim_status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="tile b-5 rise" style={{ '--i': 5 }}>
            <TileHead
              icon={<IconAlertCircle />}
              title="Top DNS Problems"
              linkText="View"
              onLink={() => navigate('/dns')}
            />
            {dnsLoading ? (
              <>
                <SkeletonRow cols={2} />
                <SkeletonRow cols={2} />
                <SkeletonRow cols={2} />
              </>
            ) : dnsProblems.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: 8, padding: '18px 0', color: 'var(--success)',
                            fontSize: 13, fontWeight: 600 }}>
                <span className="rec-glyph match"><IconCheck /></span>
                No DNS problems detected
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {dnsProblems.slice(0, 8).map((p, i) => {
                  const isCritical = p.problem.includes('Missing') || p.problem.includes('Mismatch') || p.problem.includes('Invalid');
                  const maxCount   = dnsProblems[0]?.count || 1;
                  const barWidth   = Math.round((p.count / maxCount) * 100);
                  return (
                    <div key={i} style={{ padding: '8px 0',
                                           borderBottom: i < dnsProblems.length - 1
                                             ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center', marginBottom: 5 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)',
                                       display: 'flex', alignItems: 'center', gap: 7 }}>
                          <RecordIcon status={isCritical ? 'missing' : 'warning'} />
                          {p.problem}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600,
                                       fontFamily: 'var(--font-mono)',
                                       color: isCritical ? 'var(--danger)' : 'var(--warning)',
                                       flexShrink: 0, marginLeft: 8 }}>
                          {p.count}
                        </span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2,
                                    background: 'var(--bg-badge)', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${barWidth}%`,
                          borderRadius: 2,
                          background: isCritical ? 'var(--danger)' : 'var(--warning)',
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="tile b-7 rise" style={{ '--i': 6 }}>
            <TileHead
              icon={<IconHistory />}
              title="Recent DNS Changes"
              linkText="View Email Records"
              onLink={() => navigate('/dns')}
            />
            {dnsLoading ? (
              <>
                <SkeletonRow cols={4} />
                <SkeletonRow cols={4} />
                <SkeletonRow cols={4} />
              </>
            ) : dnsChanges.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0',
                            color: 'var(--text-muted)', fontSize: 13 }}>
                No DNS changes detected yet.
                <br />
                <span style={{ fontSize: 11 }}>Changes appear when a DNS record value differs between checks.</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {dnsChanges.map((c, i) => (
                  <div key={i} style={{
                    padding: '9px 0',
                    borderBottom: i < dnsChanges.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                                  alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                                      marginBottom: 3 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '1px 7px',
                            borderRadius: 'var(--radius-xs)',
                            fontFamily: 'var(--font-mono)',
                            background: 'var(--primary-subtle)',
                            border: '1px solid var(--primary-border)',
                            color: 'var(--primary)',
                          }}>{c.record_type}</span>
                          <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)',
                                         color: 'var(--text-primary)', fontWeight: 600,
                                         overflow: 'hidden', textOverflow: 'ellipsis',
                                         whiteSpace: 'nowrap', maxWidth: 220 }}
                                title={c.domain}>{c.domain}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)',
                                      fontFamily: 'var(--font-mono)',
                                      overflow: 'hidden', textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap' }}>
                          <span style={{ color: 'var(--danger)' }}>
                            {c.old_value ? c.old_value.slice(0, 40) + (c.old_value.length > 40 ? '…' : '') : 'none'}
                          </span>
                          <span style={{ margin: '0 6px', color: 'var(--text-muted)' }}>→</span>
                          <span style={{ color: 'var(--success)' }}>
                            {c.new_value ? c.new_value.slice(0, 40) + (c.new_value.length > 40 ? '…' : '') : 'none'}
                          </span>
                        </div>
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0,
                                     marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                        {relativeTime(c.detected_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
